import { FC, memo, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { TVerificationAppFieldConfigItem } from "../../models";
import {
  conditionHasCompoundStructure,
  formatConditionInline,
  formatConditionMultiline,
  getConditionFieldError,
  isEditableSimpleCondition,
} from "./conditionHelpers";

function renderSyntaxToken(token: string, index: number) {
  if (token === "") return null;

  const sx = (() => {
    if (/^\s+$/.test(token)) return {};
    if (token === "(" || token === ")") {
      return { color: "text.secondary", fontWeight: 600 };
    }
    if (token === "AND" || token === "OR" || token === "NOT" || token === "constants") {
      return { color: "info.dark", fontWeight: 700 };
    }
    if (/^".*"$/.test(token)) {
      return { color: "success.dark" };
    }
    if (token === "=" || token === "!=" || token === ">" || token === "<" || token === "in" || token === "exists" || token === "not") {
      return { color: "warning.dark", fontWeight: 600 };
    }
    return {};
  })();

  return (
    <Box component='span' key={`${token}-${index}`} sx={sx}>
      {token}
    </Box>
  );
}

const ConditionSyntaxText: FC<{
  multiline: boolean;
  text: string;
}> = memo(({ multiline, text }) => {
  const tokens = useMemo(
    () => text.split(/(AND|OR|NOT|constants|\(|\)|"[^"\n]*"|\s+)/g),
    [text],
  );

  return (
    <Typography
      component='div'
      variant='body2'
      sx={{
        fontFamily: "Menlo, Monaco, Consolas, monospace",
        whiteSpace: multiline ? "pre-wrap" : "normal",
        overflowWrap: "anywhere",
        lineHeight: 1.55,
      }}
    >
      {tokens.map((token, index) => renderSyntaxToken(token, index))}
    </Typography>
  );
});

export const ConditionPreview: FC<{
  availableFields?: TVerificationAppFieldConfigItem[];
  condition: unknown;
  onEdit?: () => void;
  onRemove?: () => void;
  readOnlyNote?: string;
  title: string;
}> = memo(({ availableFields, condition, onEdit, onRemove, readOnlyNote, title }) => {
  const [multiline, setMultiline] = useState(false);
  const canUseMultiline = conditionHasCompoundStructure(condition);
  const displayText = multiline
    ? formatConditionMultiline(condition)
    : formatConditionInline(condition);
  const simpleFieldError = availableFields && isEditableSimpleCondition(condition)
    ? getConditionFieldError(condition, availableFields)
    : null;

  return (
    <Stack
      spacing={0.75}
      sx={{
        p: 1.25,
        border: "1px solid",
        borderColor: simpleFieldError ? "error.light" : "divider",
        borderRadius: 1.5,
        bgcolor: "grey.50",
      }}
    >
      <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
        <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {canUseMultiline ? (
          <Button size='small' onClick={() => setMultiline((prev) => !prev)}>
            {multiline ? "Single line" : "Multiline view"}
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {onEdit ? (
          <Tooltip title='Edit condition'>
            <IconButton
              size='small'
              onClick={onEdit}
              aria-label='Edit condition'
            >
              <EditOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        ) : null}
        {onRemove ? (
          <Tooltip title='Remove condition'>
            <IconButton
              size='small'
              onClick={onRemove}
              aria-label='Remove condition'
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <ConditionSyntaxText multiline={multiline} text={displayText} />
      {simpleFieldError ? (
        <Typography variant='caption' color='error'>
          {simpleFieldError}
        </Typography>
      ) : null}
      {readOnlyNote ? (
        <Typography variant='caption' color='text.secondary'>
          {readOnlyNote}
        </Typography>
      ) : null}
    </Stack>
  );
});
